import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  InputAdornment
} from '@mui/material';
import { FaSearch } from 'react-icons/fa';
import { AVAILABLE_ICONS, ICON_KEYWORDS, preloadAllIcons, getPreloadedIcon } from '../utils/iconMap';

// Create icon list using dynamic loading
const iconList = AVAILABLE_ICONS.map(iconName => ({
  name: iconName,
  keywords: ICON_KEYWORDS[iconName] || []
}));

// Component to render a preloaded icon
const PreloadedIconRenderer = ({ iconName, loaded }) => {
  if (!loaded) return '⏳';
  
  const IconComponent = getPreloadedIcon(iconName);
  return <IconComponent />;
};

const IconBrowser = ({ open, onClose, selectedIcon, onIconSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [iconsLoaded, setIconsLoaded] = useState(false);

  // Preload all icons when dialog opens
  useEffect(() => {
    if (open && !iconsLoaded) {
      console.log('Starting to preload icons...');
      preloadAllIcons().then(() => {
        console.log('Icons preloaded successfully');
        setIconsLoaded(true);
      }).catch((error) => {
        console.error('Error preloading icons:', error);
      });
    }
  }, [open, iconsLoaded]);

  const filteredIcons = useMemo(() => {
    if (!searchTerm) return iconList;
    
    const search = searchTerm.toLowerCase();
    return iconList.filter(icon => 
      icon.name.toLowerCase().includes(search) ||
      icon.keywords.some(keyword => keyword.includes(search))
    );
  }, [searchTerm]);

  const handleIconClick = (iconName) => {
    onIconSelect(iconName);
    setSearchTerm('');
    onClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: { height: '80vh' }
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Choose an Icon</Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredIcons.length} icons available
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search icons (e.g., 'admin', 'team', 'security')"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch style={{ color: '#6B7280' }} />
                  </InputAdornment>
                ),
              }
            }}
          />
        </Box>
        
        <Box sx={{ height: '50vh', overflow: 'auto' }}>
          <Grid container spacing={1}>
            {filteredIcons.map((icon) => (
              <Grid item xs={3} sm={2} md={1.5} key={icon.name}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 1,
                    border: '1px solid',
                    borderColor: selectedIcon === icon.name ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: selectedIcon === icon.name ? 'primary.light' : 'transparent',
                    '&:hover': {
                      bgcolor: selectedIcon === icon.name ? 'primary.light' : 'action.hover',
                      borderColor: 'primary.main'
                    },
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => handleIconClick(icon.name)}
                >
                  <Box sx={{ fontSize: '24px', mb: 0.5 }}>
                    <PreloadedIconRenderer iconName={icon.name} loaded={iconsLoaded} />
                  </Box>
                  <Typography 
                    variant="caption" 
                    align="center"
                    sx={{ 
                      fontSize: '10px',
                      lineHeight: 1.2,
                      color: selectedIcon === icon.name ? 'primary.contrastText' : 'text.secondary'
                    }}
                  >
                    {icon.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          
          {filteredIcons.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No icons found matching "{searchTerm}"
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try searching for terms like "admin", "team", "security", "business"
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={() => handleIconClick(selectedIcon)}
          disabled={!selectedIcon}
        >
          Select Icon
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IconBrowser;